import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { filterNil } from '@ngneat/elf';
import { DeviceErrorType } from '../models/device-manger';
import { SystemItemState, SystemItemType } from '../models/system-status';
import { SubjectMap } from '../sessions/common/session-data-structures';
import { MediaDeviceState } from '../utilities/webrtc-stats-reporter/webrtc-stats-reporter-types';
import { DeviceType } from '../common/utils/devices-handle-util';
import { PanelView } from '../sessions/panel/panel.component';
import { ParticipantEventAction } from '../common/interfaces/rtc-interface';
import { ParticipantsSystemStateService } from './participants-system-state.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { DeviceResourcesMonitoringService } from './device-resources-monitoring.service';
import { UserService } from './user.service';
import { ProviderStateService } from './provider-state.service';
import { FLAGS, FlagsService } from './flags.service';
import { RtcServiceController } from './rtc.service';
import { PresenceProvider } from './presence-provider';

export enum SystemStatusEntryPoint {
  TOP_NAV_BAR,
  PARTICIPANT_MANAGER_PANEL,
  VIDEO_TILE,
}

export interface SystemStatusUserData {
  userId: string;
  name?: string;
}

export enum InternetAndPerformaceDeviceErrorType {
  DEFAULT_ERROR = 'default',
}

@Injectable({
  providedIn: 'root',
})
export class SystemStatusPanelService {
  private _systemStatusEntryPoint$ = new BehaviorSubject<SystemStatusEntryPoint>(
    SystemStatusEntryPoint.TOP_NAV_BAR,
  );
  private systemStatesMap$: SubjectMap<SystemItemState> = new SubjectMap();
  private systemErrorsMap$: SubjectMap<
    DeviceErrorType | InternetAndPerformaceDeviceErrorType | undefined
  > = new SubjectMap();
  private _areAllStatesGood$ = new BehaviorSubject<boolean>(false);
  private _isPanelForLocal$ = new BehaviorSubject<boolean>(true);
  private _systemStatusUserData$ = new BehaviorSubject<SystemStatusUserData>({
    userId: '',
  });
  private _isSystemStatusPanelEnabled$ = new BehaviorSubject<boolean>(true);
  private isLocalDeviceStruggling = false;

  private systemStatusSub?: Subscription;
  private localDevicePerformanceSub?: Subscription;
  private participantEventsSub?: Subscription;

  constructor(
    private providerState: ProviderStateService,
    private sessionSharedDataService: SessionSharedDataService,
    private deviceResourcesMonitoringService: DeviceResourcesMonitoringService,
    private participantsSystemStateService: ParticipantsSystemStateService,
    private userService: UserService,
    private flagsService: FlagsService,
    private rtcService: RtcServiceController,
    private presenceProvider: PresenceProvider,
  ) {
    this.resetSystemStatusPanel();
    this.checkSystemStatusPanelFlag();
  }

  public initSubscriptions() {
    this.listenToParticipantEvents();
    this.initSystemStatusSubscriptions();
    this.setUpCurrentUserDevicePerformanceSubscription();
  }

  private initSystemStatusSubscriptions() {
    this.systemStatusSub = combineLatest([
      this.providerState.localParticipantId$.pipe(distinctUntilChanged()),
      this._systemStatusUserData$,
    ])
      .pipe(
        tap(([, userData]) => {
          this.setIsPanelForLocal(userData.userId);
          this.resetSystemStatusPanel();
        }),
        map(([localParticipantId, userData]) =>
          this.getInCallParticipantId(localParticipantId, userData),
        ),
        switchMap((inCallParticipantId) =>
          inCallParticipantId ? this.setUpUserSystemStatus(inCallParticipantId) : [],
        ),
      )
      .subscribe(([micState, camState, isNetworkStable, isDeviceStruggling]) => {
        this.updateSystemStatesMap(micState, camState, isNetworkStable, isDeviceStruggling);
      });
  }

  private getInCallParticipantId(
    localParticipantId: string | undefined,
    userData: SystemStatusUserData,
  ) {
    const inCallParticipant = this.rtcService.service.getInCallParticipantByUserId(userData.userId);
    if (this._isPanelForLocal$.value) {
      return localParticipantId;
    }
    return inCallParticipant?.participantId;
  }

  private updateSystemStatesMap(
    micState: MediaDeviceState,
    camState: MediaDeviceState,
    isNetworkStable: boolean,
    isDeviceStruggling: boolean,
  ) {
    this.systemStatesMap$
      .get(SystemItemType.MICROPHONE)
      .next(this.evaluateMediaDeviceStatus(micState));
    this.systemErrorsMap$.get(SystemItemType.MICROPHONE).next(micState?.errorType ?? undefined);

    this.systemStatesMap$.get(SystemItemType.CAMERA).next(this.evaluateMediaDeviceStatus(camState));
    this.systemErrorsMap$.get(SystemItemType.CAMERA).next(camState?.errorType ?? undefined);

    this.systemStatesMap$
      .get(SystemItemType.INTERNET_CONNECTION)
      .next(this.evaluateInternetConnectionStatus(isNetworkStable));

    this.systemStatesMap$
      .get(SystemItemType.DEVICE_PERFORMANCE)
      .next(this.evaluateDevicePerformanceStatus(isDeviceStruggling));

    this.checkAllStatesGood();
  }

  private setUpUserSystemStatus(participantId: string) {
    return combineLatest([
      this.participantsSystemStateService.getParticipantDeviceState$(
        DeviceType.AUDIO,
        participantId,
      ),
      this.participantsSystemStateService.getParticipantDeviceState$(
        DeviceType.VIDEO,
        participantId,
      ),
      this.participantsSystemStateService.getParticipantNetworkStability$(participantId),
      this.participantsSystemStateService.getParticipantDevicePerformance$(participantId),
    ]);
  }

  // should be used for current user system status only when user is not in call
  private setUpCurrentUserDevicePerformanceSubscription() {
    this.localDevicePerformanceSub =
      this.deviceResourcesMonitoringService.isDeviceStruggling$.subscribe((isDeviceStruggling) => {
        if (this._isPanelForLocal$.value) {
          this.isLocalDeviceStruggling = isDeviceStruggling;
          this.systemStatesMap$
            .get(SystemItemType.DEVICE_PERFORMANCE)
            .next(isDeviceStruggling ? SystemItemState.NEEDS_ATTENTION : SystemItemState.GOOD);
          this.checkAllStatesGood();
        }
      });
  }

  private checkAllStatesGood() {
    const allStatesGood = Object.values(SystemItemType).every(
      (item) => this.systemStatesMap$.get(item).value === SystemItemState.GOOD,
    );
    this._areAllStatesGood$.next(allStatesGood);
  }

  private evaluateMediaDeviceStatus(deviceState: MediaDeviceState): SystemItemState {
    if (deviceState.hasError) {
      return SystemItemState.NEEDS_ATTENTION;
    } else {
      return SystemItemState.GOOD;
    }
  }

  private evaluateInternetConnectionStatus(isNetworkStable: boolean): SystemItemState {
    if (isNetworkStable) {
      return SystemItemState.GOOD;
    } else {
      return SystemItemState.NEEDS_ATTENTION;
    }
  }

  private evaluateDevicePerformanceStatus(isDeviceStruggling: boolean): SystemItemState {
    if (isDeviceStruggling) {
      return SystemItemState.NEEDS_ATTENTION;
    } else {
      return SystemItemState.GOOD;
    }
  }

  private setIsPanelForLocal(userId: string) {
    this._isPanelForLocal$.next(userId === this.userService.user.value?.user._id);
  }

  private initSystemStatesMap() {
    this.systemStatesMap$.get(SystemItemType.MICROPHONE).next(SystemItemState.INACTIVE);
    this.systemStatesMap$.get(SystemItemType.CAMERA).next(SystemItemState.INACTIVE);
    this.systemStatesMap$.get(SystemItemType.INTERNET_CONNECTION).next(SystemItemState.INACTIVE);
    if (this._isPanelForLocal$.value) {
      this.systemStatesMap$
        .get(SystemItemType.DEVICE_PERFORMANCE)
        .next(this.evaluateDevicePerformanceStatus(this.isLocalDeviceStruggling));
    } else {
      this.systemStatesMap$.get(SystemItemType.DEVICE_PERFORMANCE).next(SystemItemState.GOOD);
    }
    this.checkAllStatesGood();
  }

  private initSystemErrorsMap() {
    this.systemErrorsMap$.get(SystemItemType.MICROPHONE).next(undefined);
    this.systemErrorsMap$.get(SystemItemType.CAMERA).next(undefined);
    this.systemErrorsMap$
      .get(SystemItemType.INTERNET_CONNECTION)
      .next(InternetAndPerformaceDeviceErrorType.DEFAULT_ERROR);
    this.systemErrorsMap$
      .get(SystemItemType.DEVICE_PERFORMANCE)
      .next(InternetAndPerformaceDeviceErrorType.DEFAULT_ERROR);
  }

  private listenToParticipantEvents() {
    this.participantEventsSub = this.providerState.participantEvents$
      .pipe(filter((participantEvent) => participantEvent?.action === ParticipantEventAction.LEFT))
      .subscribe((participantEvent) => {
        const action = participantEvent?.action;
        const participant = participantEvent?.participant;

        if (!action || !participant) {
          return;
        }

        // redirect user to participant manager if panel for a remote user is currently open,
        // and this remote user or the local user left the call.
        // The panel component stays alive (hidden) after closing, so the stored user data
        // can be stale and must not open a panel on its own.
        if (
          this.sessionSharedDataService.changeRightPanelView.value === PanelView.systemStatus &&
          !this._isPanelForLocal$.value &&
          this.shouldCloseRemoteUserPanel(participant.userId)
        ) {
          this.sessionSharedDataService.changeRightPanelView.next(PanelView.participantsManager);
        }

        // reset the panel status if local panel and current user left the call
        if (
          this._isPanelForLocal$.value &&
          participant.userId === this._systemStatusUserData$.value.userId
        ) {
          this.resetSystemStatusPanel();
        }
      });
  }

  private resetSystemStatusPanel() {
    this.initSystemStatesMap();
    this.initSystemErrorsMap();
  }

  /**
   * we should close remote user panel if the remote user or the local user left the call
   * @param userId (the user who left the call)
   */
  private shouldCloseRemoteUserPanel(userId: string): boolean {
    return (
      userId === this._systemStatusUserData$.value.userId ||
      userId === this.userService.user.value?.user._id
    );
  }

  private checkSystemStatusPanelFlag() {
    this.flagsService
      .featureFlagChanged(FLAGS.ENABLE_NETWORK_INDICATOR)
      .subscribe((isFlagEnabled) => {
        this._isSystemStatusPanelEnabled$.next(
          isFlagEnabled &&
            (this.flagsService.featureFlagsVariables[FLAGS.ENABLE_NETWORK_INDICATOR]
              ?.enable_system_status_panel as boolean),
        );
      });
  }

  // the system status panel option should be visible in the actions menu for current user
  // when he selects himself or selects other user if they are both in call
  public shouldShowSystemStatusInActionsMenu$(currentUserId: string, selectedUserId: string) {
    return this.presenceProvider.currentSpaceRoomCallPresenceActivity$.pipe(
      map(
        (inCallUsers) =>
          this.isSystemStatusPanelEnabled &&
          (selectedUserId === currentUserId ||
            (inCallUsers.has(currentUserId) && inCallUsers.has(selectedUserId))),
      ),
    );
  }

  get isSystemStatusPanelEnabled() {
    return this._isSystemStatusPanelEnabled$.value;
  }

  get systemStatusEntryPoint$(): Observable<SystemStatusEntryPoint> {
    return this._systemStatusEntryPoint$.asObservable();
  }

  set systemStatusEntryPoint(systemStatusEntryPoint: SystemStatusEntryPoint) {
    this._systemStatusEntryPoint$.next(systemStatusEntryPoint);
  }

  set systemStatusUserData(systemStatusUserDate: SystemStatusUserData) {
    this._systemStatusUserData$.next(systemStatusUserDate);
  }

  get systemStatusUserData$() {
    return this._systemStatusUserData$.asObservable();
  }

  get isPanelForLocal$() {
    return this._isPanelForLocal$.asObservable();
  }

  get isPanelForLocal() {
    return this._isPanelForLocal$.value;
  }

  get areAllStatesGood$() {
    return this._areAllStatesGood$.asObservable();
  }

  public getSystemStatesMap$ = (item: SystemItemType) =>
    this.systemStatesMap$.get(item).pipe(filterNil(), startWith(SystemItemState.INACTIVE));

  public getSystemErrorsMap$ = (item: SystemItemType) =>
    this.systemErrorsMap$.get(item).pipe(filterNil(), startWith(undefined));

  public cleanService() {
    this.systemStatusSub?.unsubscribe();
    this.participantEventsSub?.unsubscribe();
    this.localDevicePerformanceSub?.unsubscribe();
  }
}
